import NotFoundView from 'features/NotFoundView'
import GettingStartedView from 'features/Onboarding/getting-started/GettingStartedView'
import EmployeeSignUpView from 'features/Users/EmployeeSignUpView'
import { KC_EMPLOYEE_SIGNUP_FORM_URL } from 'features/KingCounty/utils'
import EmployerSignUpView from 'features/Users/EmployerSignUpView'
import LoginView from 'features/Users/LoginView'
import ResetPasswordView from 'features/Users/ResetPasswordView'
import React, { useEffect } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import DefaultLayout from './DefaultLayout'
import { Locations } from './Locations'
import EmployerEmailConfirmationView from 'features/Users/EmployerEmailConfirmationView'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { Elements } from '@stripe/react-stripe-js'
import { getStripePromise } from 'redux/config/services/Constants'
import Playground from 'features/Playground'
import MCPConsentView from 'features/MCP/MCPConsentView'
import EmployeeRoutes from './EmployeeRoutes'
import AdminRoutes from './AdminRoutes'
import { getPlatformType } from './Utils'
import Loading from 'core-system/Loading'
import { routerActions } from 'shared/Router/RouterActions'
import ExternalRedirect from 'shared/Router/ExternalRedirect'

const stripeElementsOptions = {
  fonts: [
    {
      family: 'PolySans, sans-serif',
      src: `url('https://fonts.googleapis.com/css2?family=Hind&display=swap') format("woff2")`,
    },
  ],
}

const AllRoutes = () => (
  <BrowserRouter>
    <Routes>
      <Route path={Locations.Login} element={<LoginView />} />
      <Route path={Locations.MCPConsent} element={<MCPConsentView />} />
      <Route path={Locations.ResetPassword} element={<ResetPasswordView />} />
      <Route path={Locations.EmployeeSignUp} element={<EmployeeSignUpView />} />
      <Route
        path={Locations.KcEmployeeSignUp}
        element={<ExternalRedirect to={KC_EMPLOYEE_SIGNUP_FORM_URL} />}
      />
      <Route path={Locations.EmployerSignUp} element={<EmployerSignUpView />} />
      <Route
        path={Locations.EmployerEmailConfirmation}
        element={<EmployerEmailConfirmationView />}
      />
      <Route
        path={'/'}
        element={<DefaultLayout showHeaderNav={false} showSidebar={false} />}
      >
        <Route
          path={Locations.GettingStarted}
          element={<GettingStartedView />}
        />
      </Route>
      {EmployeeRoutes()}
      {AdminRoutes()}

      {process.env.NODE_ENV === 'development' && (
        <Route path='/playground' element={<Playground />} />
      )}
      <Route path='*' element={<NotFoundView />} />
    </Routes>
  </BrowserRouter>
)

const RootRoutes = () => {
  const dispatch = useDispatch()
  const platformType = getPlatformType()
  const { connectedAccount, mode } = useSelector(
    (state: AppState) => state.router
  )

  useEffect(() => {
    dispatch(routerActions.updateConnectedAccount())
  }, [platformType])

  // If we don't know which mode to use, show the loading screen
  // to fetch the connected account from the backend
  // because the call is on DefaultLayout
  if (mode === 'fetching') {
    return <Loading fullPage withoutNav />
  }

  // If we don't have a connected account, use the root account
  if (mode === 'root') {
    return (
      <Elements stripe={getStripePromise()} options={stripeElementsOptions}>
        <AllRoutes />
      </Elements>
    )
  }

  // If we have a connected account, use it
  if (mode === 'connected') {
    return (
      <Elements
        stripe={getStripePromise(connectedAccount)}
        options={stripeElementsOptions}
      >
        <AllRoutes />
      </Elements>
    )
  }
}

RootRoutes.displayName = 'RootRoutes'

export default RootRoutes
